import React, { useEffect, useRef, useState } from "react";

import {
  downloadControlBulkUploadTemplate,
  importControlBulkUploadWorkbook,
} from "../../api/controls.api.js";
import Button from "../../ui/Button";
import Modal from "../../ui/Modal";
import { colors, spacing, typography } from "../../ui/tokens";

const XLSX_MIME_TYPE = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

const summaryCardStyle = {
  border: `1px solid ${colors.border}`,
  borderRadius: 10,
  padding: 12,
  display: "grid",
  gap: 10,
  background: colors.surface,
};

const getDownloadErrorMessage = (error) => {
  const status = Number(error?.status || 0);
  const detail = String(error?.detail || error?.message || "").trim();

  if (status === 403) {
    return "You do not have permission to download the control bulk upload template.";
  }
  if (status === 404) {
    return "The control bulk upload template endpoint is not available on the running backend. Restart the backend and try again.";
  }
  if (status >= 500) {
    return detail || "The server could not generate the control bulk upload template.";
  }
  if (!status) {
    return "The control bulk upload template request could not reach the backend. Check that the API server is running and reachable.";
  }
  return detail || "Unable to download the control bulk upload template.";
};

export default function ControlBulkUploadModal({ open, onClose, onImported }) {
  const inputRef = useRef(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileError, setFileError] = useState("");
  const [requestError, setRequestError] = useState("");
  const [uploading, setUploading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [result, setResult] = useState(null);

  const resetState = () => {
    setSelectedFile(null);
    setFileError("");
    setRequestError("");
    setUploading(false);
    setDownloading(false);
    setResult(null);
    if (inputRef.current) {
      inputRef.current.value = "";
    }
  };

  useEffect(() => {
    if (!open) {
      resetState();
    }
  }, [open]);

  const handleClose = () => {
    resetState();
    onClose?.();
  };

  const handleDownloadTemplate = async () => {
    setRequestError("");
    setDownloading(true);
    try {
      const response = await downloadControlBulkUploadTemplate();
      const blob = response?.blob instanceof Blob ? response.blob : new Blob([response?.blob || response], { type: XLSX_MIME_TYPE });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = response?.filename || "control-bulk-upload-template.xlsx";
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setRequestError(getDownloadErrorMessage(err));
    } finally {
      setDownloading(false);
    }
  };

  const handleFileChange = (event) => {
    const nextFile = event?.target?.files?.[0] || null;
    setSelectedFile(nextFile);
    setFileError("");
    setRequestError("");
    setResult(null);

    if (!nextFile) return;
    if (!String(nextFile.name || "").toLowerCase().endsWith(".xlsx")) {
      setSelectedFile(null);
      setFileError("Upload a `.xlsx` workbook created from the Glyder control bulk upload template.");
      event.target.value = "";
    }
  };

  const handleImport = async () => {
    if (!selectedFile || fileError) return;
    setUploading(true);
    setRequestError("");
    setResult(null);
    try {
      const payload = await importControlBulkUploadWorkbook(selectedFile);
      setResult(payload);
      if (payload?.success) {
        onImported?.(payload);
        setSelectedFile(null);
        if (inputRef.current) {
          inputRef.current.value = "";
        }
      }
    } catch (err) {
      setRequestError(err?.response?.data?.detail || err?.message || "Unable to import the control workbook.");
    } finally {
      setUploading(false);
    }
  };

  if (!open) return null;

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title="Bulk Upload Controls"
      description="Download the Glyder template, complete the workbook, and upload the finished `.xlsx` file. The import validates every populated row before creating any controls."
      size="lg"
      footer={
        <>
          <Button onClick={handleClose} variant="secondary" disabled={uploading}>
            Close
          </Button>
          <Button onClick={handleDownloadTemplate} variant="secondary" disabled={uploading || downloading}>
            {downloading ? "Downloading..." : "Download Template"}
          </Button>
          <Button onClick={handleImport} variant="primary" disabled={!selectedFile || !!fileError || uploading}>
            {uploading ? "Importing..." : "Import Controls"}
          </Button>
        </>
      }
    >
      <div style={{ display: "grid", gap: spacing.md }}>
        <div style={{ fontSize: typography.size.sm, color: colors.textMuted }}>
          v2 supports `.xlsx` only. Linked Risk IDs are optional, up to three control label columns are supported, and review or test fields can be imported alongside the control. Fully blank rows are ignored. The import is all-or-nothing, so no controls are created if any populated row fails validation.
        </div>

        <label style={{ display: "grid", gap: spacing.xs }}>
          <span style={{ fontWeight: typography.weight.medium }}>Completed Workbook</span>
          <input
            ref={inputRef}
            type="file"
            accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            onChange={handleFileChange}
            disabled={uploading}
            aria-label="Completed Workbook"
          />
        </label>

        {selectedFile && (
          <div style={{ fontSize: typography.size.sm }}>
            Selected file: <strong>{selectedFile.name}</strong>
          </div>
        )}
        {fileError && <div style={{ color: colors.danger }}>{fileError}</div>}
        {requestError && <div role="alert" style={{ color: colors.danger }}>{requestError}</div>}

        {result && (
          <div style={summaryCardStyle}>
            <div style={{ fontWeight: typography.weight.bold }}>
              {result.success ? "Import completed" : "Import not completed"}
            </div>
            {result.message && (
              <div style={{ fontSize: typography.size.sm, color: result.success ? colors.success : colors.textMuted }}>
                {result.message}
              </div>
            )}
            <div style={{ display: "flex", flexWrap: "wrap", gap: 12, fontSize: typography.size.sm }}>
              <span>Processed: {result.rows_processed || 0}</span>
              <span>Valid: {result.rows_valid || 0}</span>
              <span>Created: {result.rows_created || 0}</span>
              <span>Failed: {result.rows_failed || 0}</span>
              <span>Empty rows skipped: {result.rows_skipped_empty || 0}</span>
            </div>

            {Array.isArray(result.file_errors) && result.file_errors.length > 0 && (
              <div>
                <div style={{ fontWeight: typography.weight.medium, marginBottom: 4 }}>File errors</div>
                <ul style={{ margin: 0, paddingLeft: 18 }}>
                  {result.file_errors.map((item, index) => (
                    <li key={`file-error-${index}`}>{item}</li>
                  ))}
                </ul>
              </div>
            )}

            {Array.isArray(result.header_errors) && result.header_errors.length > 0 && (
              <div>
                <div style={{ fontWeight: typography.weight.medium, marginBottom: 4 }}>Header errors</div>
                <ul style={{ margin: 0, paddingLeft: 18 }}>
                  {result.header_errors.map((item, index) => (
                    <li key={`header-error-${index}`}>{item}</li>
                  ))}
                </ul>
              </div>
            )}

            {Array.isArray(result.row_errors) && result.row_errors.length > 0 && (
              <div>
                <div style={{ fontWeight: typography.weight.medium, marginBottom: 4 }}>Row errors</div>
                <div
                  style={{
                    maxHeight: 220,
                    overflowY: "auto",
                    border: `1px solid ${colors.border}`,
                    borderRadius: 8,
                    padding: 8,
                  }}
                >
                  <ul style={{ margin: 0, paddingLeft: 18 }}>
                    {result.row_errors.map((item, index) => (
                      <li key={`row-error-${index}`}>
                        Row {item.row_number}
                        {item.field ? ` - ${item.field}` : ""}
                        : {item.message}
                        {item.value ? ` (${item.value})` : ""}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
}
